import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const VisitorCounter = ({ 
  position = 'bottom-right', 
  showDetails = false, 
  compact = false,
  offsetX = 0,  // Add this
  offsetY = 0   // Add this
}) => {
  const [stats, setStats] = useState({
    total_unique_visitors: 0,
    total_visits: 0,
    total_page_views: 0,
    today_unique: 0,
    today_visits: 0
  });
  const [loading, setLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [error, setError] = useState(null);

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const trackVisitor = useCallback(async () => {
    try {
      const page = window.location.pathname;
      const response = await fetch(`https://nlngraclub.com/nlnapi/visitor_counter.php?action=track&page=${encodeURIComponent(page)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.stats) {
          setStats(prev => ({
            ...prev,
            total_unique_visitors: data.stats.total_unique_visitors || 0,
            total_visits: data.stats.total_visits || 0,
            total_page_views: data.stats.total_page_views || 0
          }));
        }
      }
    } catch (error) {
      console.error('Error tracking visitor:', error);
      setError(error.message);
    }
  }, []);

  const fetchTodayStats = useCallback(async () => {
    try {
      const response = await fetch('https://nlngraclub.com/nlnapi/visitor_counter.php?action=get_today');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(prev => ({
            ...prev,
            today_unique: (data.today_stats && data.today_stats.unique_visitors) || 0,
            today_visits: (data.today_stats && data.today_stats.total_visits) || 0,
            total_unique_visitors: (data.total_stats && data.total_stats.total_unique_visitors) || prev.total_unique_visitors,
            total_visits: (data.total_stats && data.total_stats.total_visits) || prev.total_visits,
            total_page_views: (data.total_stats && data.total_stats.total_page_views) || prev.total_page_views
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Track visitor on page load
    trackVisitor();
    
    // Fetch today's stats
    fetchTodayStats();
    
    // Set up periodic refresh every 30 seconds
    const interval = setInterval(() => {
      fetchTodayStats();
    }, 30000);
    
    // Animate counter on mount
    setTimeout(() => setAnimate(true), 500);
    
    return () => clearInterval(interval);
  }, [trackVisitor, fetchTodayStats]);

const getPositionClasses = () => {
  switch(position) {
    case 'top-left': return { top: `${16 + offsetY}px`, left: `${16 + offsetX}px` };
    case 'top-right': return { top: `${16 + offsetY}px`, right: `${16 + offsetX}px` };
    case 'bottom-left': return { bottom: `${16 + offsetY}px`, left: `${16 + offsetX}px` };
    default: return { bottom: `${16 + offsetY}px`, right: `${16 + offsetX}px` };
  }
};
  // Don't render if there's an error (silently fail)
  if (error && !compact) {
    return null;
  }

  if (compact) {
    return (
      <div className="fixed z-50" style={getPositionClasses()}>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.05 }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            borderRadius: '9999px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            padding: '8px 16px',
            cursor: 'pointer',
            border: '1px solid #e5e7eb'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <i className="fas fa-users" style={{ color: '#2e7d32' }}></i>
            <span style={{ fontWeight: 600, color: '#374151' }}>
              {formatNumber(stats.total_unique_visitors)}
            </span>
            <AnimatePresence>
              {showTooltip && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    right: 0,
                    marginBottom: '8px',
                    backgroundColor: '#1f2937',
                    color: 'white',
                    fontSize: '12px',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    whiteSpace: 'nowrap',
                    zIndex: 50
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div>Total Visitors: {stats.total_unique_visitors.toLocaleString()}</div>
                    <div style={{ color: '#9ca3af', fontSize: '10px' }}>Today: {stats.today_unique}</div>
                  </div>
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    right: '16px',
                    transform: 'translateY(50%) rotate(45deg)',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#1f2937'
                  }}></div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    );
  }

  if (showDetails) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        className="fixed z-50"
        style={getPositionClasses()}
      >
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
          border: '1px solid #f3f4f6',
          padding: '16px',
          width: '256px',
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f3f4f6'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-chart-line" style={{ color: '#2e7d32' }}></i>
              <h3 style={{ fontWeight: 600, color: '#1f2937', margin: 0 }}>Site Visitors</h3>
            </div>
            {animate && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                style={{ width: '8px', height: '8px', backgroundColor: '#22c55e', borderRadius: '9999px' }}
              />
            )}
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#6b7280', fontSize: '14px' }}>Total Visitors</span>
              <motion.span 
                key={stats.total_unique_visitors}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}
              >
                {stats.total_unique_visitors.toLocaleString()}
              </motion.span>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#6b7280', fontSize: '14px' }}>Total Visits</span>
              <span style={{ fontSize: '18px', fontWeight: 600, color: '#374151' }}>
                {stats.total_visits.toLocaleString()}
              </span>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#6b7280', fontSize: '14px' }}>Page Views</span>
              <span style={{ fontSize: '18px', fontWeight: 600, color: '#374151' }}>
                {stats.total_page_views.toLocaleString()}
              </span>
            </div>
            
            <div style={{ paddingTop: '8px', borderTop: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Today's Visitors</span>
                <motion.span 
                  key={stats.today_unique}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  style={{ fontWeight: 600, color: '#16a34a' }}
                >
                  +{stats.today_unique}
                </motion.span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Today's Visits</span>
                <span style={{ fontSize: '14px', color: '#4b5563' }}>{stats.today_visits}</span>
              </div>
            </div>
            
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #2e7d32',
                  borderTopColor: 'transparent',
                  borderRadius: '9999px',
                  animation: 'spin 1s linear infinite'
                }}></div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // Default floating badge style
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.05 }}
      className="fixed z-50 cursor-pointer group"
      style={getPositionClasses()}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div style={{
        background: 'linear-gradient(135deg, #2e7d32, #1b5e20)',
        color: 'white',
        borderRadius: '9999px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <i className="fas fa-user-friends"></i>
        <span style={{ fontWeight: 600 }}>
          {formatNumber(stats.total_unique_visitors)}
        </span>
        <i className="fas fa-eye" style={{ fontSize: '11px', opacity: 0.75 }}></i>
      </div>
      
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            style={{
              position: 'absolute',
              bottom: '100%',
              right: 0,
              marginBottom: '12px',
              backgroundColor: '#1f2937',
              color: 'white',
              fontSize: '14px',
              borderRadius: '8px',
              padding: '8px 16px',
              minWidth: '180px',
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
              zIndex: 50
            }}
          >
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontWeight: 600 }}>Site Statistics</div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>Total Visitors</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{stats.total_unique_visitors.toLocaleString()}</div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Active Today</div>
              <div style={{ color: '#4ade80', fontWeight: 600 }}>{stats.today_unique} visitors</div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: 0,
              right: '20px',
              transform: 'translateY(50%) rotate(45deg)',
              width: '8px',
              height: '8px',
              backgroundColor: '#1f2937'
            }}></div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </motion.div>
  );
};

export default VisitorCounter;